import { forwardRef, useEffect, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { DeleteOutline } from '@mui/icons-material';
import { Box, IconButton, Link as MuiLink, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SystemColors } from 'melp-design/style';
import { Button } from '../button';
import { SortableList } from '../sortable-list';
import { TextInput } from '../text-input';
import { Typography } from '../typography';
import {
  isSameVideo,
  lookupVideo,
  parseVideoUrl,
  youtubeThumbnailUrl,
  type ParsedVideo,
  type VideoMetadata,
  type VideoSource,
} from './video-input.utils';

const SOURCE_LABEL: Record<VideoSource, string> = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
};

interface VideoDetails extends VideoMetadata {
  source: VideoSource;
}

interface Props {
  value?: string[];
  onChange?: (videos: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  maxVideos?: number;
}

const videosKeyOf = (videos: string[]) => videos.join('\u0000');

const detailsFromLookup = (
  parsed: ParsedVideo,
  metadata: VideoMetadata,
): VideoDetails => ({
  source: parsed.source,
  title: metadata.title,
  thumbnailUrl: metadata.thumbnailUrl,
});

interface VideoRowProps {
  url: string;
  details?: VideoDetails;
  showHandle: boolean;
  disabled?: boolean;
  onRemove: () => void;
}

const VideoRow = ({
  url,
  details,
  showHandle,
  disabled,
  onRemove,
}: VideoRowProps) => {
  const { t } = useTranslation();
  const parsed = parseVideoUrl(url);
  const source = details?.source ?? parsed?.source;
  const title = details?.title ?? url;
  const thumbnailUrl =
    details?.thumbnailUrl ??
    (parsed?.source === 'youtube' ? youtubeThumbnailUrl(parsed.id) : undefined);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      py={1}
      width="100%"
      minWidth={0}
    >
      {showHandle ? <SortableList.DragIndicator height={45} /> : null}

      <Box
        sx={{
          width: 80,
          height: 45,
          borderRadius: '4px',
          overflow: 'hidden',
          flexShrink: 0,
          bgcolor: SystemColors.grey[96],
          border: `1px solid ${SystemColors.grey[90]}`,
        }}
      >
        {thumbnailUrl ? (
          <Box
            component="img"
            src={thumbnailUrl}
            alt=""
            draggable={false}
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : null}
      </Box>

      <Box flex={1} minWidth={0}>
        <MuiLink
          href={url}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          sx={{
            color: SystemColors.black,
            display: 'block',
            maxWidth: '100%',
            '&:hover': { color: SystemColors.black },
          }}
        >
          <Typography
            variant="h3"
            component="span"
            noWrap
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
        </MuiLink>

        {source ? (
          <Typography variant="p2" color="textSecondary">
            {SOURCE_LABEL[source]}
          </Typography>
        ) : null}
      </Box>

      <IconButton
        size="small"
        disabled={disabled}
        title={t('common.remove')}
        aria-label={t('common.remove')}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
        sx={{ color: SystemColors.black, flexShrink: 0 }}
      >
        <DeleteOutline />
      </IconButton>
    </Stack>
  );
};

export const VideoInput = forwardRef<HTMLInputElement, Props>(
  (
    { value, onChange, label, placeholder, disabled, maxVideos = 1 },
    forwardedRef,
  ) => {
    const { t } = useTranslation();
    const videos = value ?? [];
    const videosKey = videosKeyOf(videos);
    const canAdd = videos.length < maxVideos;
    const showHandle = maxVideos > 1;

    const [draft, setDraft] = useState('');
    const [invalid, setInvalid] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [detailsByUrl, setDetailsByUrl] = useState<
      Record<string, VideoDetails>
    >({});

    const detailsRef = useRef(detailsByUrl);
    const requestRef = useRef(0);
    const checkingRef = useRef(false);
    const isMountedRef = useRef(true);

    useEffect(
      () => () => {
        isMountedRef.current = false;
      },
      [],
    );

    const rememberDetails = (url: string, details: VideoDetails) => {
      detailsRef.current = { ...detailsRef.current, [url]: details };
      setDetailsByUrl(detailsRef.current);
    };

    useEffect(() => {
      const urls = videosKey ? videosKey.split('\u0000') : [];
      let cancelled = false;

      urls.forEach((url) => {
        if (detailsRef.current[url]) return;

        const parsed = parseVideoUrl(url);
        if (!parsed) return;

        void lookupVideo(parsed).then((lookup) => {
          if (cancelled || detailsRef.current[url]) return;

          rememberDetails(
            url,
            lookup.result === 'invalid'
              ? {
                  source: parsed.source,
                  title: parsed.url,
                  thumbnailUrl:
                    parsed.source === 'youtube'
                      ? youtubeThumbnailUrl(parsed.id)
                      : undefined,
                }
              : detailsFromLookup(parsed, lookup.metadata),
          );
        });
      });

      return () => {
        cancelled = true;
      };
    }, [videosKey]);

    const updateVideos = (next: string[]) => {
      onChange?.(next);
    };

    const handleAdd = async () => {
      const raw = draft.trim();
      if (!raw || checkingRef.current || disabled || !canAdd) return;

      const parsed = parseVideoUrl(raw);
      if (!parsed) {
        setInvalid(true);
        return;
      }

      const duplicate = videos.some((existing) => {
        const existingParsed = parseVideoUrl(existing);
        return existingParsed ? isSameVideo(existingParsed, parsed) : false;
      });
      if (duplicate) {
        setDraft('');
        setInvalid(false);
        return;
      }

      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      checkingRef.current = true;
      setIsChecking(true);
      setInvalid(false);

      const lookup = await lookupVideo(parsed);

      if (!isMountedRef.current || requestId !== requestRef.current) return;

      checkingRef.current = false;
      setIsChecking(false);

      if (lookup.result === 'invalid') {
        setInvalid(true);
        return;
      }

      rememberDetails(parsed.url, detailsFromLookup(parsed, lookup.metadata));
      setDraft('');
      updateVideos([...videos, parsed.url]);
    };

    const rows = videos.map((url) => (
      <VideoRow
        key={url}
        url={url}
        details={detailsByUrl[url]}
        showHandle={showHandle}
        disabled={disabled}
        onRemove={() => updateVideos(videos.filter((item) => item !== url))}
      />
    ));

    return (
      <Stack gap={1} width="100%">
        {canAdd ? (
          <TextInput
            ref={forwardedRef}
            value={draft}
            label={label}
            placeholder={placeholder}
            disabled={disabled}
            errorMessage={
              invalid ? t('components.video_input.error') : undefined
            }
            infoMessage={invalid ? undefined : t('components.video_input.info')}
            onChange={(next) => {
              setDraft(next);
              setInvalid(false);
              requestRef.current += 1;
              checkingRef.current = false;
              setIsChecking(false);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              handleAdd();
            }}
            InputProps={{
              endAdornment: (
                <Button
                  type="button"
                  label={t('common.add')}
                  size="sm"
                  variant="primary"
                  loading={isChecking}
                  disabled={disabled || isChecking || !draft.trim()}
                  onClick={() => {
                    handleAdd();
                  }}
                />
              ),
            }}
          />
        ) : null}

        {videos.length ? (
          showHandle ? (
            <SortableList
              key={videosKey}
              disabled={disabled}
              onPositionChange={(source, destination) =>
                updateVideos(arrayMove(videos, source, destination))
              }
              items={videos.map((url, index) => ({
                id: url,
                content: rows[index],
              }))}
            />
          ) : (
            <Stack>{rows}</Stack>
          )
        ) : null}
      </Stack>
    );
  },
);

VideoInput.displayName = 'VideoInput';
